import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Lightning, Trophy, Clock, TrendUp } from "@phosphor-icons/react";
import { ToolCTA, InlineNewsletter, ScoreCTA } from "@/components/InlineCTA";
import SEO from "@/components/SEO";
import PublicNav from "@/components/PublicNav";
import { DTMBarChart, DTMLineChart, TOKENS } from "@/components/charts";
const API = process.env.REACT_APP_BACKEND_URL;

export default function TradeDeadlineGuide() {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/deadline/analysis`)
      .then(r => r.ok ? r.json() : null)
      .then(setData)
      .catch(() => {});
  }, []);

  const cs = data?.champion_stats;
  const weekly = data?.weekly_comparison;

  // Build in-season weekly chart data (weeks 2-17)
  const weeklyChart = weekly
    ?.filter(w => w.week >= 2 && w.week <= 17)
    ?.map(w => ({
      x: `Wk ${w.week}`,
      week: w.week,
      trades: w.champions_per_league,
    })) || [];

  // Compute in-season stats
  const inSeason = weekly?.filter(w => w.week >= 2 && w.week <= 17) || [];
  const totalInSeason = inSeason.reduce((s, w) => s + w.champions, 0);
  const deadlineTotal = inSeason.filter(w => w.week >= 9 && w.week <= 13).reduce((s, w) => s + w.champions, 0);
  const deadlinePct = totalInSeason > 0 ? (100 * deadlineTotal / totalInSeason).toFixed(1) : "0";
  const peakWeek = inSeason.reduce((max, w) => w.champions_per_league > (max?.champions_per_league || 0) ? w : max, null);
  const postDeadlineTotal = inSeason.filter(w => w.week > 13).reduce((s, w) => s + w.champions, 0);
  const postDeadlinePct = totalInSeason > 0 ? (100 * postDeadlineTotal / totalInSeason).toFixed(1) : "0";

  // Bar chart: deadline window vs rest
  const periodChart = totalInSeason > 0 ? [
    { label: "Pre-Deadline (Wk 2-8)", value: parseFloat((100 * inSeason.filter(w => w.week < 9).reduce((s, w) => s + w.champions, 0) / totalInSeason).toFixed(1)) },
    { label: "Deadline (Wk 9-13)", value: parseFloat(deadlinePct), highlight: true },
    { label: "Post-Deadline (Wk 14-17)", value: parseFloat(postDeadlinePct) },
  ] : [];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO
        title="The Dynasty Trade Deadline Guide: What Champions Actually Did — Dynasty Trade Maker"
        description="We analyzed 167,000+ real trades from 6,976 championship-winning Sleeper leagues to show exactly what champions did at the trade deadline. Data-backed deadline strategy for contenders and rebuilders."
        path="/guide/dynasty-trade-deadline" image="https://dynastytrademaker.com/hero-images/guide-trade-deadline.jpg"
      />
      <PublicNav />
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Link to="/guide" className="text-[10px] text-[#555] hover:text-[#a3a3a3] no-underline uppercase tracking-wider">Guide</Link>
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#ccff00] block mt-4 mb-2" data-testid="deadline-data-label">
          DATA FROM {data ? (data.total_trades || 0).toLocaleString() : "167,000+"} REAL TRADES
        </span>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-white uppercase mb-4" data-testid="deadline-title">
          THE TRADE DEADLINE GUIDE: <span className="text-[#ccff00]">WHAT CHAMPIONS ACTUALLY DID</span>
        </h1>

        {/* Hero Image */}
        <div className="mb-6 border border-[#262626] rounded-sm overflow-hidden">
          <img src="/hero-images/guide-trade-deadline.jpg" alt="" className="w-full h-auto" loading="eager" />
        </div>

        <div className="text-sm text-[#a3a3a3] leading-relaxed space-y-6">
          <p>The trade deadline is the most pressure-packed moment of the dynasty season. Contenders feel the window closing. Rebuilders smell opportunity. And most managers make deadline moves on instinct, then spend the offseason wondering if they blew it.</p>
          <p>We took a different approach. We pulled the real deadline behavior of {data?.champion_leagues?.toLocaleString() || "6,976"} championship-winning Sleeper leagues to see exactly what the champions did, not guesses, real data from {(data?.total_trades || 167265).toLocaleString()} trades.</p>

          {/* Section: Why Deadline Matters */}
          <h2 className="font-heading text-2xl text-white uppercase mt-10 mb-3">WHY THE DEADLINE MATTERS MORE IN DYNASTY</h2>
          <p>In redraft, a deadline trade is a rental. In dynasty, every deadline trade is permanent. The player you acquire is yours going forward, and the pick or player you give up is gone for good.</p>
          <p>That raises the stakes both ways. A great deadline buy can anchor your roster for years. A panic overpay can set you back just as long. The deadline is a fork that affects your whole future, which is exactly why it should be done with data instead of adrenaline.</p>

          {/* Section: What Champions Did */}
          <h2 className="font-heading text-2xl text-white uppercase mt-10 mb-3">WHAT CHAMPIONS DID AT THE DEADLINE</h2>
          <p>Here is the question that actually matters: were the leagues that produced champions more active at the deadline? The data says yes, clearly.</p>

          {cs && (
            <div className="border border-[#262626] bg-[#171717] p-5 my-4">
              <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#ccff00] mb-3 flex items-center gap-1.5">
                <Trophy size={14} weight="fill" /> CHAMPION LEAGUE DEADLINE BEHAVIOR
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold font-mono text-[#ccff00]">{cs.pct_leagues_deadline_active}%</div>
                  <div className="text-[10px] text-[#888]">made a deadline trade</div>
                </div>
                <div>
                  <div className="text-2xl font-bold font-mono text-white">{cs.avg_deadline_trades_per_league}</div>
                  <div className="text-[10px] text-[#888]">deadline trades / league</div>
                </div>
                <div>
                  <div className="text-2xl font-bold font-mono text-white">{cs.avg_total_trades_per_league}</div>
                  <div className="text-[10px] text-[#888]">total trades / league</div>
                </div>
                <div>
                  <div className="text-2xl font-bold font-mono text-white">{deadlinePct}%</div>
                  <div className="text-[10px] text-[#888]">of in-season trades at deadline</div>
                </div>
              </div>
            </div>
          )}

          <p><strong className="text-white">{cs?.pct_leagues_deadline_active || 77.9}% of champion leagues made at least one deadline trade.</strong> These leagues averaged {cs?.avg_deadline_trades_per_league || 3.15} trades during the deadline window (weeks 9-13) and {cs?.avg_total_trades_per_league || 23.98} trades over the full season. {deadlinePct}% of all in-season trading activity happened in the deadline window alone.</p>
          <p>The lesson is that sitting on your hands at the deadline is not what winning leagues do. Championship-producing leagues were active, decisive traders throughout the deadline window.</p>

          {/* Weekly Trade Volume Chart */}
          {weeklyChart.length > 0 && (
            <DTMLineChart
              data={weeklyChart}
              title="WEEKLY TRADE VOLUME (CHAMPION LEAGUES)"
              subtitle="Average trades per league by NFL week. Deadline window highlighted."
              source={`${(data?.total_trades || 0).toLocaleString()} trades from ${(data?.champion_leagues || 0).toLocaleString()} champion leagues`}
              series={[{ key: "trades", name: "Trades per League", color: TOKENS.green }]}
              xKey="x"
              height={220}
              formatter={v => typeof v === "number" ? v.toFixed(2) : v}
              annotations={[
                { x: "Wk 9", label: "Deadline Opens", color: TOKENS.muted },
                { x: "Wk 13", label: "Deadline Closes", color: TOKENS.muted },
              ]}
            />
          )}

          {peakWeek && (
            <p>Trade activity peaks at <strong className="text-white">Week {peakWeek.week}</strong> with {peakWeek.champions_per_league.toFixed(2)} trades per league, the heart of the deadline. After week 13, volume drops sharply. Only {postDeadlinePct}% of in-season trades happen after the deadline closes. The message is clear: if you are going to make your move, make it during the window, not after.</p>
          )}

          {/* Deadline vs Rest Chart */}
          {periodChart.length > 0 && (
            <DTMBarChart
              data={periodChart}
              title="WHERE IN-SEASON TRADES HAPPEN"
              subtitle="Share of in-season trades by period in champion leagues."
              source={`${totalInSeason.toLocaleString()} in-season trades`}
              layout="vertical"
              height={140}
              defaultColor={TOKENS.greyBar}
              formatter={v => `${v}%`}
            />
          )}

          {/* Contender Playbook */}
          <h2 className="font-heading text-2xl text-white uppercase mt-10 mb-3">THE CONTENDER PLAYBOOK</h2>
          <p>If you are in the hunt, the deadline is your moment to push. But pushing well is different from pushing recklessly. Here is what the data supports.</p>
          <div className="space-y-3 my-4">
            {[
              { icon: <TrendUp size={14} weight="bold" className="text-[#4ecdc4]" />, title: "Buy the piece that fits, not the biggest name", desc: "Champions acquired players that filled a real need on their roster, not the flashiest available star. Know your one weakness and target it." },
              { icon: <Clock size={14} weight="bold" className="text-[#45b7d1]" />, title: "Buy before prices spike", desc: "The value of win-now players climbs as the deadline nears. Move early, while sellers are still reasonable, and you pay less for the same help." },
              { icon: <Lightning size={14} weight="bold" className="text-[#f9ca24]" />, title: "Pay a premium, not your whole future", desc: "A title is worth paying up for, but a contender who empties the future to add a marginal piece can win one year and crater the next." },
              { icon: <Trophy size={14} weight="bold" className="text-[#ccff00]" />, title: "Know the real price", desc: "Check what a player is actually worth before you offer, so you are paying a fair premium instead of getting fleeced in your own championship push." },
            ].map(item => (
              <div key={item.title} className="border border-[#262626] bg-[#171717] p-4 flex gap-3">
                <div className="mt-0.5 shrink-0">{item.icon}</div>
                <div>
                  <p className="text-sm text-white font-semibold mb-1">{item.title}</p>
                  <p className="text-xs text-[#888]">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Rebuilder Playbook */}
          <h2 className="font-heading text-2xl text-white uppercase mt-10 mb-3">THE REBUILDER PLAYBOOK</h2>

          <ToolCTA text="Are you a contender or a rebuilder?" subtext="Import your league and get your contender ranking instantly." buttonText="SCORE MY TEAM" buttonLink="/pricing" icon={<Trophy size={12} weight="fill" />} />
          <p>If you are not contending, the deadline is the best day of your season, because it is when your assets are worth the most.</p>
          <div className="space-y-3 my-4">
            {[
              { title: "Sell into desperation", desc: "Contenders get less rational as the deadline approaches. Your aging veteran is worth more right now than at any other point in the year." },
              { title: "Turn present into future", desc: "Every win-now vet you hold is losing value. Convert them into the youngest, highest-upside players and picks you can get." },
              { title: "Keep your young core", desc: "Sell the players whose value will decline, but keep the young pieces who will still be good in your next window. A total teardown is rarely the fastest path back." },
            ].map(item => (
              <div key={item.title} className="border border-[#262626] bg-[#171717] p-4">
                <p className="text-sm text-white font-semibold mb-1">{item.title}</p>
                <p className="text-xs text-[#888]">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* The Mistake */}
          <h2 className="font-heading text-2xl text-white uppercase mt-10 mb-3">THE MISTAKE BOTH SIDES MAKE</h2>

          <InlineNewsletter context="deadline" source="deadline-guide" />
          <p>The single biggest deadline error, for buyers and sellers alike, is not knowing the real value of what is changing hands. Buyers overpay because they are emotional. Sellers undersell because they are impatient. The champions were not smarter or luckier. They just made moves that were correctly valued, something anyone can do with the right numbers in front of them.</p>

          {/* CTA */}
          <div className="border border-[#ccff00]/30 bg-[#ccff00]/5 p-6 text-center my-8" data-testid="deadline-cta">
            <h3 className="font-heading text-lg text-white uppercase mb-2">MAKE THE CORRECTLY-VALUED MOVE</h3>
            <p className="text-xs text-[#888] mb-4">Run any deadline deal through our calculator first, so you know the price is right before you commit.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/trade-calculator" className="h-9 px-5 bg-[#ccff00] text-black rounded-sm text-[11px] inline-flex items-center gap-1.5 no-underline font-bold hover:bg-[#b8e600] transition-colors">
                <Lightning size={12} weight="fill" /> VALUE ANY DEADLINE TRADE, FREE
              </Link>
              <Link to="/championship-blueprint" className="text-[11px] text-[#ccff00] no-underline hover:underline flex items-center gap-1">
                See if your roster is title-ready <ArrowRight size={11} />
              </Link>
            </div>
          </div>

          {/* FAQ */}
          <h2 className="font-heading text-2xl text-white uppercase mt-10 mb-3">FREQUENTLY ASKED QUESTIONS</h2>
          <div className="space-y-3" itemScope itemType="https://schema.org/FAQPage">
            {[
              { q: "Should I buy or sell at the dynasty trade deadline?", a: `It depends on whether you are contending. ${cs?.pct_leagues_deadline_active || 77.9}% of champion leagues were active at the deadline. If you are contending, buy the piece that fits your weakness. If you are rebuilding, sell veterans while contenders are desperate.` },
              { q: "What did championship teams do at the deadline?", a: `${cs?.pct_leagues_deadline_active || 77.9}% of champion leagues made a deadline trade, averaging ${cs?.avg_deadline_trades_per_league || 3.15} trades during weeks 9-13. They were decisive and correctly valued their moves.` },
              { q: "When is the best time to make a deadline trade?", a: `Buyers should move early in the window before prices spike. In our data, trade activity peaked at Week ${peakWeek?.week || 11}. Sellers should wait closer to the deadline when buyers are most desperate.` },
              { q: "How much should I pay up at the deadline?", a: "A title is worth a premium, but not your whole future. Pay up for a piece that fills a real need, and check its actual value first so the premium is fair." },
              { q: "I am rebuilding. Should I sell everything at the deadline?", a: "Sell the veterans whose value will decline, since they are worth the most when contenders are desperate. Keep young players who will matter in your next window." },
            ].map((f, i) => (
              <div key={i} className="border border-[#262626] bg-[#171717] p-4" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <p className="text-sm text-white font-semibold mb-1" itemProp="name">{f.q}</p>
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p className="text-xs text-[#888]" itemProp="text">{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Related */}
        <div className="mt-8 space-y-2">
          <p className="text-[9px] font-bold uppercase tracking-wider text-[#555]">RELATED</p>
          {[
            { href: "/tools/trade-fairness", label: "Trade Fairness Analyzer", desc: "Is your trade fair? Data from 125K graded trades" },
            { href: "/guide/dynasty-startup-draft", label: "Data-Backed Draft Guide", desc: "What actually wins in startup drafts" },
            { href: "/research/championship-report", label: "Championship Report", desc: "Every finding from 6,976 champions" },
            { href: "/trades", label: "Real Trade Explorer", desc: "Browse 167K+ real Sleeper trades" },
          ].map(l => (
            <Link key={l.href} to={l.href} className="flex items-center justify-between p-3 border border-[#262626] bg-[#171717] hover:border-[#ccff00]/30 no-underline rounded-sm transition-colors">
              <div><p className="text-xs text-white font-semibold">{l.label}</p><p className="text-[10px] text-[#666]">{l.desc}</p></div>
              <ArrowRight size={12} className="text-[#555]" />
            </Link>
          ))}
        </div>

        <div className="mt-6 text-center text-[10px] text-[#444] space-x-3">
          <Link to="/guide" className="hover:text-[#a3a3a3] no-underline">Guides</Link>
          <Link to="/rankings" className="hover:text-[#a3a3a3] no-underline">Rankings</Link>
          <Link to="/trade-calculator" className="hover:text-[#a3a3a3] no-underline">Calculator</Link>
        </div>
      </div>
    </div>
  );
}
